import { createSlice, createSelector } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { of } from "rxjs";
import { switchMap, map, catchError } from "rxjs/operators";
import { BetTournamentsRequestField } from "./client/bet-tournaments.client.js";
import { BET_TOURNAMENTS_FEATURE_KEY, BetTournamentUserParticipationStatus } from "./common/bet-tournaments.common.js";
import { bettorsTournamentPrizes } from "./prizes.js";
const initialBetTournamentsState = {
  tournaments: [],
  leaderboards: [],
  users: [],
  authorizedUsers: [],
  currentTournamentId: 0,
  participationStatuses: {},
  loaded: false,
  error: null,
  prizes: bettorsTournamentPrizes
};
const betTournamentsSlice = createSlice({
  name: BET_TOURNAMENTS_FEATURE_KEY,
  initialState: initialBetTournamentsState,
  reducers: {
    getBetTournamentsStart: (state, action) => {
      state.loaded = false;
    },
    getBetTournamentsSuccess: (state, action) => {
      const data = action.payload;
      state.loaded = true;
      if (BetTournamentsRequestField.tournaments in data) {
        state.tournaments = data[BetTournamentsRequestField.tournaments];
        if (state.currentTournamentId === 0 && data[BetTournamentsRequestField.tournaments].length) {
          state.currentTournamentId = data[BetTournamentsRequestField.tournaments].reduce(
            (acc, tournament) => tournament.id > acc ? tournament.id : acc,
            data[BetTournamentsRequestField.tournaments][0].id
          );
        }
      }
      if (BetTournamentsRequestField.leaderboards in data) {
        state.leaderboards = data[BetTournamentsRequestField.leaderboards];
      }
      if (BetTournamentsRequestField.users in data) {
        state.users = data[BetTournamentsRequestField.users];
      }
      if (BetTournamentsRequestField.authorizedUsers in data) {
        state.authorizedUsers = data[BetTournamentsRequestField.authorizedUsers];
      }
    },
    getBetTournamentsFailure: (state, action) => {
      state.loaded = true;
      state.error = action.payload;
    },
    setCurrentTournament: (state, action) => {
      state.currentTournamentId = action.payload;
    },
    getIsParticipateBetTournamentStart: (state, action) => {
    },
    getIsParticipateBetTournamentSuccess: (state, action) => {
      const updatedStatuses = action.payload.reduce(
        (acc, item) => {
          acc[item.tournamentId] = item.value;
          return acc;
        },
        {}
      );
      state.participationStatuses = {
        ...state.participationStatuses,
        ...updatedStatuses
      };
    },
    getIsParticipateBetTournamentFailure: (state, action) => {
    },
    changeParticipationStatusStart: (state, action) => {
      const { tournamentId, value } = action.payload;
      const parsedValue = value ? BetTournamentUserParticipationStatus.agreed : BetTournamentUserParticipationStatus.disagreed;
      const updatedStatus = { [tournamentId]: parsedValue };
      state.participationStatuses = {
        ...state.participationStatuses,
        ...updatedStatus
      };
    },
    changeParticipationStatusSuccess: (state, action) => {
    },
    changeParticipationStatusFailure: (state, action) => {
    },
    clearBetTournamentUserState: (state, action) => {
      state.authorizedUsers = [];
      state.participationStatuses = {};
    }
  }
});
const betTournamentsReducer = betTournamentsSlice.reducer;
const {
  getBetTournamentsStart,
  getBetTournamentsSuccess,
  getBetTournamentsFailure,
  setCurrentTournament,
  getIsParticipateBetTournamentStart,
  getIsParticipateBetTournamentSuccess,
  getIsParticipateBetTournamentFailure,
  changeParticipationStatusStart,
  changeParticipationStatusSuccess,
  changeParticipationStatusFailure,
  clearBetTournamentUserState
} = betTournamentsSlice.actions;
const getBetTournamentsPlatformState = (rootState) => rootState[BET_TOURNAMENTS_FEATURE_KEY];
const selectBetTournamentsList = createSelector(
  getBetTournamentsPlatformState,
  (s) => s.tournaments
);
const selectBetTournamentsLeaderboards = createSelector(
  getBetTournamentsPlatformState,
  (s) => s.leaderboards
);
const selectBetTournamentsAuthorizedUsers = createSelector(
  getBetTournamentsPlatformState,
  (s) => s.authorizedUsers
);
const selectBetTournamentsNextUser = createSelector(
  getBetTournamentsPlatformState,
  (s, currentUser) => currentUser,
  (s, currentUser) => currentUser && currentUser.place < 1 ? s.users.find(
    (user) => user.leaderboardId === currentUser.leaderboardId && user.place === currentUser.place + 1
  ) : null
);
const selectBetTournamentsUsers = createSelector(
  getBetTournamentsPlatformState,
  (s) => s.users
);
const selectBetTournamentsCurrentTournamentId = createSelector(
  getBetTournamentsPlatformState,
  (s) => {
    const idToSwitch = null;
    return idToSwitch && s.tournaments.find((tournament) => tournament.id === idToSwitch) ? idToSwitch : s.currentTournamentId;
  }
);
const selectBetTournamentsCurrentTournament = createSelector(
  [selectBetTournamentsCurrentTournamentId, selectBetTournamentsList],
  (id, list) => id ? list.find((tournament) => tournament.id === id) : null
);
const selectBetTournamentsCurrentLeaderboards = createSelector(
  [selectBetTournamentsCurrentTournamentId, selectBetTournamentsLeaderboards],
  (currentTournamentId, leaderboards) => leaderboards.reduce(
    (acc, current) => current.tournamentId === currentTournamentId ? { ...acc, [current.colorScheme]: current } : acc,
    {}
  )
);
const selectBetTournamentSingleBoardLeaderboard = createSelector(
  selectBetTournamentsCurrentLeaderboards,
  (leaderboards) => leaderboards == null ? void 0 : leaderboards[1]
);
const selectBetTournamentsUsersByLeaderboardId = createSelector(
  selectBetTournamentsUsers,
  (users, leaderboardId) => leaderboardId,
  (users, leaderboardId) => users.reduce(
    (acc, current) => current.leaderboardId === leaderboardId ? { ...acc, [current.place]: current } : acc,
    {}
  )
);
const selectBetTournamentsCurrentUserByLeaderboardId = createSelector(
  selectBetTournamentsAuthorizedUsers,
  (users, leaderboardId) => leaderboardId,
  (users, leaderboardId) => users.find((user) => user.leaderboardId === leaderboardId)
);
const selectBetTournamentsLoaded = createSelector(
  getBetTournamentsPlatformState,
  (s) => s.loaded
);
const selectBetTournamentsError = createSelector(
  getBetTournamentsPlatformState,
  (s) => s.error
);
const selectBetTournamentsParticipationStatuses = createSelector(
  getBetTournamentsPlatformState,
  (s) => s.participationStatuses
);
const selectBetTournamentParticipationStatus = createSelector(
  [
    selectBetTournamentsParticipationStatuses,
    selectBetTournamentsCurrentTournamentId
  ],
  (statuses, tournamentId) => tournamentId ? statuses[tournamentId] : null
);
const selectBetTournamentPrizes = createSelector(
  getBetTournamentsPlatformState,
  (s) => s.prizes
);
const getBetTournamentsData = (params) => ({
  type: getBetTournamentsStart.type,
  payload: params
});
const betTournamentsListEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getBetTournamentsStart.type),
  switchMap((action) => Api.betTournament.getBetTournaments(
    action.payload
  ).pipe(
    map((data) => getBetTournamentsSuccess(data)),
    catchError((err) => of(getBetTournamentsFailure(err)))
  ))
);
const getIsParticipateBetTournament = (params) => ({ type: getIsParticipateBetTournamentStart.type, payload: params });
const IsParticipateBetTournamentEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getIsParticipateBetTournamentStart.type),
  switchMap((action) => Api.betTournament.getIsParticipate(action.payload).pipe(
    map((data) => getIsParticipateBetTournamentSuccess(data)),
    catchError((err) => of(getIsParticipateBetTournamentFailure(err)))
  ))
);
const setBetTournamentParticipationStatus = (params) => ({ type: changeParticipationStatusStart.type, payload: params });
const SetBetTournamentParticipationStatusEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(changeParticipationStatusStart.type),
  switchMap((action) => Api.betTournament.changeParticipationStatus(action.payload).pipe(
    map((data) => changeParticipationStatusSuccess(data)),
    catchError((err) => of(changeParticipationStatusFailure(err)))
  ))
);
export {
  IsParticipateBetTournamentEpic,
  SetBetTournamentParticipationStatusEpic,
  betTournamentsListEpic,
  betTournamentsReducer,
  betTournamentsSlice,
  changeParticipationStatusFailure,
  changeParticipationStatusStart,
  changeParticipationStatusSuccess,
  clearBetTournamentUserState,
  getBetTournamentsData,
  getBetTournamentsFailure,
  getBetTournamentsPlatformState,
  getBetTournamentsStart,
  getBetTournamentsSuccess,
  getIsParticipateBetTournament,
  getIsParticipateBetTournamentFailure,
  getIsParticipateBetTournamentStart,
  getIsParticipateBetTournamentSuccess,
  initialBetTournamentsState,
  selectBetTournamentParticipationStatus,
  selectBetTournamentPrizes,
  selectBetTournamentSingleBoardLeaderboard,
  selectBetTournamentsAuthorizedUsers,
  selectBetTournamentsCurrentLeaderboards,
  selectBetTournamentsCurrentTournament,
  selectBetTournamentsCurrentTournamentId,
  selectBetTournamentsCurrentUserByLeaderboardId,
  selectBetTournamentsError,
  selectBetTournamentsLeaderboards,
  selectBetTournamentsList,
  selectBetTournamentsLoaded,
  selectBetTournamentsNextUser,
  selectBetTournamentsParticipationStatuses,
  selectBetTournamentsUsers,
  selectBetTournamentsUsersByLeaderboardId,
  setBetTournamentParticipationStatus,
  setCurrentTournament
};
